import { useState, useEffect, useCallback, useRef } from 'react';
import { editHistoryService } from '../services/editHistory';
import { EditHistory } from '../types';

interface UseUndoRedoOptions {
  audioFileId: string;
  onOperationComplete?: (operation: any) => void;
  onError?: (error: string) => void;
}

export const useUndoRedo = ({
  audioFileId,
  onOperationComplete,
  onError,
}: UseUndoRedoOptions) => {
  const [editHistory, setEditHistory] = useState<EditHistory | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingHistoryRef = useRef(false);

  // Load edit history
  const loadEditHistory = useCallback(async () => {
    if (isLoadingHistoryRef.current) return; // Prevent concurrent loads

    try {
      isLoadingHistoryRef.current = true;
      const history = await editHistoryService.getEditHistory(audioFileId);
      setEditHistory(history);
    } catch (error: any) {
      onError?.(error.message || 'Failed to load edit history');
    } finally {
      isLoadingHistoryRef.current = false;
    }
  }, [audioFileId, onError]); // Clean dependency array

  // Undo operation
  const undo = useCallback(async () => {
    if (!editHistory?.canUndo) return;

    try {
      setIsLoading(true);
      const editOperation = await editHistoryService.undo(audioFileId);

      // Refresh the full edit history to update highlighting
      await loadEditHistory();

      onOperationComplete?.(editOperation);
    } catch (error: any) {
      onError?.(error.message || 'Failed to undo operation');
    } finally {
      setIsLoading(false);
    }
  }, [
    audioFileId,
    onOperationComplete,
    onError,
    editHistory?.canUndo,
    loadEditHistory,
  ]);

  // Redo operation
  const redo = useCallback(async () => {
    if (!editHistory?.canRedo) return;

    try {
      setIsLoading(true);
      const editOperation = await editHistoryService.redo(audioFileId);

      // Refresh the full edit history to update highlighting
      await loadEditHistory();

      onOperationComplete?.(editOperation);
    } catch (error: any) {
      onError?.(error.message || 'Failed to redo operation');
    } finally {
      setIsLoading(false);
    }
  }, [
    audioFileId,
    onOperationComplete,
    onError,
    editHistory?.canRedo,
    loadEditHistory,
  ]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+Z or Cmd+Z for undo
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key === 'z' &&
        !event.shiftKey
      ) {
        event.preventDefault();
        undo();
      }
      // Ctrl+Shift+Z or Cmd+Shift+Z for redo
      else if (
        (event.ctrlKey || event.metaKey) &&
        event.key === 'z' &&
        event.shiftKey
      ) {
        event.preventDefault();
        redo();
      }
      // Ctrl+Y or Cmd+Y for redo (alternative)
      else if ((event.ctrlKey || event.metaKey) && event.key === 'y') {
        event.preventDefault();
        redo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // Load history only when audioFileId changes
  useEffect(() => {
    if (!audioFileId) return;

    // Create an immediate async function to avoid stale closure issues
    const loadHistory = async () => {
      if (isLoadingHistoryRef.current) return;

      try {
        isLoadingHistoryRef.current = true;
        const history = await editHistoryService.getEditHistory(audioFileId);
        setEditHistory(history);
      } catch (error: any) {
        onError?.(error.message || 'Failed to load edit history');
      } finally {
        isLoadingHistoryRef.current = false;
      }
    };

    loadHistory();
  }, [audioFileId, onError]);

  // Refresh history when operations are performed outside this hook
  const refreshHistory = useCallback(() => {
    loadEditHistory();
  }, [loadEditHistory]);

  return {
    editHistory,
    isLoading,
    canUndo: editHistory?.canUndo ?? false,
    canRedo: editHistory?.canRedo ?? false,
    undo,
    redo,
    refreshHistory,
  };
};
